<!-- Apply custom styles to the root container -->
<div [ngStyle]="customStyles()" class="table-container">

  <!-- Table menu section -->
  @if (config().showTableMenu) {
    <div class="flex flex-row flex-wrap py-2 table-menu">
      <!-- Search bar component -->
      @if (config().enableGlobalFilter) {
        <div class="flex-none min-w-[119px] w-[25%] search-container">
          <search-bar [globalFilter]="globalFilter" >
          </search-bar>
        </div>
      }

      <div class="grow menu-spacer"></div>

      <!-- Column customization component -->
      @if (config().enableColumnResizing || config().enableColumnPinning) {
        <div class="flex-none column-edit-container">
          <column-edit [table]="table" [config]="columnEditConfig" ></column-edit>
        </div>
      }
    </div>
  }

  <!-- Main table container -->
  <div class="bg-background table-wrapper-custom table-wrapper">
    <table
      [style]="columnSizeVars()"
      part="table"
      oriTable
    >
      <!-- Table header -->
      <thead  class="table-header" oriTableHeader>
        <tr class="header-row" oriTableRow>
          @for (header of table().getHeaderGroups()[0].headers; track header.id; let i = $index) {
            <th
              class="h-11 header-cell"
              [ngStyle]="config().enableColumnPinning && header.column.getIsPinned() && header.column.getIsLastColumn('left') ? getCommonPinningStyles(header.column, i) : {}"
              [style.width.px]="header.getSize()"
              [attr.data-column-id]="header.id"
              oriTableHead
            >
              @if (!header.isPlaceholder) {
                @if (colWait) {
                  <ngx-skeleton-loader
                    count="1"
                    [theme]="{
                      'animation-duration': '2s',
                      'background': 'var(--table-skeleton-bg)',
                      'position': 'relative',
                      'height': '70%',
                      'width': '100%',
                      'border-radius': '5px',
                      'box-shadow': '0 4px 30px rgba(0, 0, 0, 0.1)',
                      'backdrop-filter': 'blur(5px)',
                      '-webkit-backdrop-filter': 'blur(5px)',
                      'margin': '0 auto',
                      'display': 'block'
                    }"
                  >
                  </ngx-skeleton-loader>
                }
                @else {
                  <div
                    [class]="
                      cn(
                        header.column.getCanSort() && config().enableSorting &&
                        'flex h-full items-center justify-between gap-2 select-none'
                      )
                    "
                    [attr.tabIndex]="header.column.getCanSort() && config().enableSorting ? 0 : undefined"
                    class="header-content"
                  >
                    <span
                      class="truncate header-text"
                      *flexRender="
                        header.column.columnDef.header;
                        props: header.getContext();
                        let headerValue
                      "
                    >
                      {{ headerValue }}
                    </span>
                    
                    <!-- Sorting indicators -->
                    @if (config().enableSorting && header.column.getCanSort()) {
                      <button 
                        oriButton 
                        variant="outline" 
                        size="icon"
                        class=" w-[25px] h-[25px] sort-button"
                       >
                        <div 
                          class="cursor-pointer sort-icon-container"
                          (click)="config().enableSorting && header.column.getToggleSortingHandler()?.($event)"
                        >
                          @switch (header.column.getIsSorted()) {
                            @case ('asc') {
                              <lucide-angular
                                [img]="ChevronUp"
                                size="16"
                                class="sort-icon-asc"
                              />
                            }
                            @case ('desc') {
                              <lucide-angular
                                [img]="ChevronDown"
                                size="16"
                                class="sort-icon-desc"
                              />
                            }
                          }
                          @if (!header.column.getIsSorted()) {
                            <lucide-angular
                              [img]="ChevronsUpDown"
                              size="16"
                              class="sort-icon-unsorted"
                            />
                          }
                        </div>
                      </button>
                    }
                  </div>
                }

                <!-- Column resize handle -->
                @if (config().enableColumnResizing) {
                  <div
                    class="resizer"
                    [class.isResizing]="header.column.getIsResizing()"
                    (dblclick)="header.column.resetSize()"
                    (mousedown)="header.getResizeHandler()($event)"
                    (touchstart)="header.getResizeHandler()($event)"
                  >
                  </div>
                  
                  <div
                    class="absolute right-0 top-0 h-full w-[5px] cursor-col-resize"
                    [class.isResizing]="header.column.getIsResizing()"
                    (dblclick)="header.column.resetSize()"
                    (mousedown)="header.getResizeHandler()($event)"
                    (touchstart)="header.getResizeHandler()($event)"
                  >
                  </div>
                }
              }
            </th>
          }
        </tr>
      </thead>

      <!-- Table body -->
      <tbody class="table-body" oriTableBody>
        @if (rowWait) {
          @for (skeleton of [].constructor(10); track $index) {
            <tr class="skeleton-row" >
              @for (header of table().getHeaderGroups()[0].headers; track header.id; let i = $index) {
                <td
                  [ngStyle]="config().enableColumnPinning && header.column.getIsPinned() ? getCommonPinningStyles(header.column, i) : {}"
                  class="skeleton-cell"
                  [attr.data-column-id]="header.id"
                  oriTableCell
                >
                  <ngx-skeleton-loader
                    count="1"
                    [theme]="{
                      'animation-duration': 'var(--table-skeleton-animation-duration)',
                      'background': 'var(--table-skeleton-bg)',
                      'position': 'relative',
                      'height': '20px',
                      'width': '100%',
                      'border-radius': '5px',
                      'margin': '0px 0',
                      'box-shadow': '0 4px 30px rgba(1, 1, 1, 0.1)',
                      'backdrop-filter': 'blur(5px)',
                      '-webkit-backdrop-filter': 'blur(5px)'
                    }"
                  >
                  </ngx-skeleton-loader>
                </td>
              }
            </tr>
          }
        }
        @else {
          @if (table().getRowModel().rows.length) {
            @for (row of table().getRowModel().rows; track row.id) {
              <tr
                [attr.data-state]="row.getIsSelected() && config().enableRowSelection && 'selected'"
                [attr.data-row-index]="row.index"
                class="table-row"
                (click)="onRowClick($event, row)"
              >
                @for (cell of row.getVisibleCells(); track cell.id; let i = $index) {
                  <td
                    [ngStyle]="config().enableColumnPinning && cell.column.getIsPinned() ? getCommonPinningStyles(cell.column, i) : {}"
                    class="table-cell"
                    [attr.data-column-id]="cell.column.id"
                    [attr.data-cell-type]="cell.column.columnDef.meta?.colType"
                    oriTableCell
                  >
                    <ng-container
                      *flexRender="
                        cell.column.columnDef.cell;
                        props: cell.getContext();
                        let cellValue
                      "
                    >
                      <div
                        class="truncate cell-content"
                        [innerHTML]="cellValue"
                      >
                      </div>
                    </ng-container>
                  </td>
                }
              </tr>
            }
          }
          @else {
            <tr class="hover:!bg-transparent w-full empty-row border-none" oriTableRow>
              <td 
                [attr.colspan]="table().getVisibleFlatColumns().length"
                class="hover:!bg-transparent p-0 !border-none"
                oriTableCell
              >
                <div class="flex items-center justify-center w-full h-[300px] max-h-100">
                  <span class="empty-text">No results.</span>
                </div>
              </td>
            </tr>
          }
        }
      </tbody>
    </table>
  </div>

  <!-- Pagination section -->
  @if (config().enablePagination) {
    <!-- Simple pagination mode -->
    @if (simplePagination) {
      <div class="flex items-center justify-end gap-4 px-2 py-2">
        <nav class="flex items-center gap-2">
            <button class="text-sm px-2 py-1 disabled:opacity-40 text-[#00358E] flex font-medium leading-[18px] items-center gap-1" 
              [class.text-gray-400]="!table().getCanPreviousPage()"
              [disabled]="!table().getCanPreviousPage()" 
              (click)="simplePrev()" aria-label="Previous page">
                <lucide-angular [img]="ChevronLeft" size="16" />
                <span>Previous</span>
            </button>
          <ng-container *ngFor="let p of simplePageNumbers()">
              <button
                class="text-sm px-2 text-[#00358E] font-medium leading-[18px] pb-[2px] pt-1 bg-transparent relative"
                [class.text-black]="p === simpleCurrentPage"
                (click)="simpleGoTo(p)"
                aria-label="Go to page {{p}}"
              >
                {{ p }}
                @if (p === simpleCurrentPage) {
                  <div class="absolute bottom-0 left-[2px] w-4/5 h-[3px] bg-[#FFBE00] rounded-[2px]"></div>
                }
              </button>
          </ng-container>
            <button class="text-sm px-2 py-1 disabled:opacity-40 text-[#00358E] font-medium leading-[18px] flex items-center gap-1" [disabled]="!table().getCanNextPage()" (click)="simpleNext()" aria-label="Next page">
              <span>Next</span>
              <lucide-angular [img]="ChevronRight" size="16" />
            </button>
        </nav>
      </div>
    }
    <!-- Full pagination mode (existing UI) -->
    @else {
      <div class="flex items-center justify-between gap-8 pagination-container">
        <!-- Results per page selector -->
        <div class="flex items-center gap-3 page-size-container">
          <label
            class="max-sm:sr-only page-size-label"
            htmlFor="table-18-select"
            oriLabel
          >
            Rows per page
          </label>
          <ori-select-native
            id="table-18-select"
            [value]="table().getState().pagination.pageSize.toString()"
            (onValueChange)="handlePageSizeChange($event)"
            class="page-size-select"
          >
            @for (pageSize of config().pageSizeOptions || [5, 10, 25, 50]; track pageSize) {
              <option [value]="pageSize" class="page-size-option">{{ pageSize }}</option>
            }
          </ori-select-native>
        </div>
        
        <!-- Page number information -->
        <div class="text-muted-foreground flex grow justify-end text-sm whitespace-nowrap pagination-info">
          <p
            class="text-muted-foreground text-sm whitespace-nowrap pagination-text"
            aria-live="polite"
          >
            <span class="text-foreground pagination-current-range">
              {{ table().getState().pagination.pageIndex * table().getState().pagination.pageSize + 1 }}-{{ this.currentPageEnd() }}
            </span>
            {{ ' ' }} of
            <span class="text-foreground pagination-total">
              {{ table().getRowCount().toString() }}
            </span>
          </p>
        </div>
        
        <!-- Pagination buttons -->
        <div class="pagination-buttons">
          <nav class="pagination-nav" oriPagination>
            <ul class="pagination-list" oriPaginationContent>
              <li class="pagination-item">
                <button
                  class="disabled:pointer-events-none disabled:opacity-50 pagination-first-button"
                  [disabled]="!table().getCanPreviousPage()"
                  (click)="table().firstPage()"
                  oriButton
                  variant="outline"
                  size="icon"
                  aria-label="Go to first page"
                >
                  <lucide-angular [img]="ChevronFirst" size="16" class="pagination-first-icon" />
                </button>
              </li>
              <li class="pagination-item">
                <button
                  class="disabled:pointer-events-none disabled:opacity-50 pagination-prev-button"
                  [disabled]="!table().getCanPreviousPage()"
                  (click)="table().previousPage()"
                  oriButton
                  variant="outline"
                  size="icon"
                  aria-label="Go to previous page"
                >
                  <lucide-angular [img]="ChevronLeft" size="16" class="pagination-prev-icon" />
                </button>
              </li>
              <li class="pagination-item">
                <button
                  [disabled]="!table().getCanNextPage()"
                  (click)="table().nextPage()"
                  oriButton
                  variant="outline"
                  size="icon"
                  aria-label="Go to next page"
                  class="pagination-next-button"
                >
                  <lucide-angular [img]="ChevronRight" size="16" class="pagination-next-icon" />
                </button>
              </li>
              <li class="pagination-item">
                <button
                  [disabled]="!table().getCanNextPage()"
                  (click)="table().lastPage()"
                  oriButton
                  variant="outline"
                  size="icon"
                  aria-label="Go to last page"
                  class="pagination-last-button"
                >
                  <lucide-angular [img]="ChevronLast" size="16" class="pagination-last-icon" />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    }
  }

  <!-- Debug information -->
  @if (config().enableDebug) {
    <div class="mt-4 p-4 bg-gray-100 rounded-md text-xs">
      <details>
        <summary class="cursor-pointer font-semibold">Debug Information</summary>
        <div class="mt-2 space-y-2">
          <div ><strong>Total Rows:</strong> {{ table().getRowCount() }}</div>
          <div ><strong>Filtered Rows:</strong> {{ table().getFilteredRowModel().rows.length }}</div>
          <div ><strong>Current Page:</strong> {{ table().getState().pagination.pageIndex + 1 }}</div>
          <div ><strong>Page Size:</strong> {{ table().getState().pagination.pageSize }}</div>
          <div ><strong>Sorting:</strong> {{ table().getState().sorting | json }}</div>
          @if (config().enableRowSelection) {
            <div ><strong>Selected Rows:</strong> {{ table().getSelectedRowModel().rows.length }}</div>
          }
          @if (config().enableGlobalFilter) {
            <div ><strong>Global Filter:</strong> "{{ table().getState().globalFilter || 'none' }}"</div>
          }
          <div ><strong>Column Count:</strong> {{ table().getAllColumns().length }}</div>
          <div ><strong>Visible Column Count:</strong> {{ table().getVisibleFlatColumns().length }}</div>
        </div>
      </details>
    </div>
  }
</div>